import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faChartPie, faDollarSign, faPoo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation } from 'react-router-dom';
import { truncate } from 'lodash';

import { addReferrerToUrl } from 'lib/utils/navigationUtils';
import {
  anchovy,
  bodyText,
  softBlue,
  ghostWhite,
  slateGrey,
  warmBlue,
  softPurple,
  softPurpleDark,
  softGreen,
  softGreenDark,
  borderGray,
  borderLightGray,
  white,
} from 'lib/css/colors';
import { fsaRebateDollar } from 'images/icons/fsa-monotone-svg-icons/fsaRebateDollar';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { titleCase } from 'lib/utils/StringProcess';
import UPBL5PercentageGMVBadge from 'components/upb_viewer/UPBL5PercentageGMVBadge';
import UPBScoreBadge from 'components/upb_viewer/UPBScoreBadge';
import { UPBViewContext } from 'components/upb_viewer/UPBViewContainer';

const DISPLAY_NAME_LENGTH = 40;

function UPBProductCatalogCard({ product, productType }) {
  const { mfrView, viewScore, viewIcon, viewGMV, InvoiceAndOrderingGMV } =
    useContext(UPBViewContext);
  const location = useLocation();

  // Include current location as referrer for back navigation
  const currentPath = `${location.pathname}${location.search}`;
  const baseProductLink = `/upb-pdp/${product.fsa_id || product.id}/${productType}/${
    viewGMV ? 'true' : 'false'
  }`;
  const productLink = addReferrerToUrl(baseProductLink, currentPath);

  const productImageUrl =
    product.thumbnail_url ??
    product.default_image_url ??
    product.propagated_thumbnail_image_url ??
    placeholderProductImage;

  const showPriceIcon =
    mfrView &&
    (product.min_unit_price > 0 ||
      product.max_unit_price > 0 ||
      product.avg_unit_price > 0 ||
      product.median_unit_price > 0);

  // Calculate M20% Min and M20% Max based on median
  const m20Min = product.median_unit_price ? product.median_unit_price * 0.8 : 0;
  const m20Max = product.median_unit_price ? product.median_unit_price * 1.2 : 0;

  return (
    <Card className={classNames('position-relative', css(styles.card))}>
      {/* Header with icons and badges */}
      <div className={css(styles.cardHeader)}>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          {viewIcon && productImageUrl.includes('google-scraped-global-product-default-images') && (
            <FontAwesomeIcon icon={faPoo} className={css(styles.editIcon)} />
          )}
          {mfrView && (product.cashback_retain || product.cashback_acquire) && (
            <OverlayTrigger
              placement="bottom"
              popperConfig={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [60, 8],
                    },
                  },
                ],
              }}
              overlay={
                <Tooltip id={`cashback-tooltip-${product.id}`}>
                  <div className={css(styles.pricingTooltip)}>
                    <div className={css(styles.tooltipTitle)}>Cashback Information</div>
                    {product.cashback_retain && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>Retain:</span>
                        <span className={css(styles.tooltipValue)}>
                          ${product.cashback_retain.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {product.cashback_acquire && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>Acquire:</span>
                        <span className={css(styles.tooltipValue)}>
                          ${product.cashback_acquire.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </Tooltip>
              }
            >
              <div className={css(styles.rebateIcon)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 256 256"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d={fsaRebateDollar.icon[4]} fill="currentColor" />
                </svg>
              </div>
            </OverlayTrigger>
          )}
          {showPriceIcon && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id={`pricing-tooltip-${product.id}`}>
                  <div className={css(styles.pricingTooltip)}>
                    <div className={css(styles.tooltipTitle)}>Pricing Information</div>
                    {product.min_unit_price > 0 && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>Min:</span>
                        <span className={css(styles.tooltipValue)}>
                          ${product.min_unit_price.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {product.max_unit_price > 0 && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>Max:</span>
                        <span className={css(styles.tooltipValue)}>
                          ${product.max_unit_price.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {product.avg_unit_price > 0 && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>Avg:</span>
                        <span className={css(styles.tooltipValue)}>
                          ${product.avg_unit_price.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {product.median_unit_price > 0 && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>Median:</span>
                        <span className={css(styles.tooltipValue)}>
                          ${product.median_unit_price.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {m20Min > 0 && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>M20% Min:</span>
                        <span className={css(styles.tooltipValue)}>${m20Min.toFixed(2)}</span>
                      </div>
                    )}
                    {m20Max > 0 && (
                      <div className={css(styles.tooltipRow)}>
                        <span className={css(styles.tooltipLabel)}>M20% Max:</span>
                        <span className={css(styles.tooltipValue)}>${m20Max.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <hr className={css(styles.tooltipSeparator)} />
                  <div className={css(styles.tooltipRow)}>
                    <span className={css(styles.tooltipLabel)}>L4 - Median:</span>
                    <span className={css(styles.tooltipValue)}>
                      ${product?.l4_median_unit_price?.toFixed(2)}
                    </span>
                  </div>
                  <div className={css(styles.tooltipRow)}>
                    <span className={css(styles.tooltipLabel)}>L4 - M20% Min:</span>
                    <span className={css(styles.tooltipValue)}>
                      ${product?.l4_median_unit_price_min?.toFixed(2)}
                    </span>
                  </div>
                  <div className={css(styles.tooltipRow)}>
                    <span className={css(styles.tooltipLabel)}>L4 - M20% Max:</span>
                    <span className={css(styles.tooltipValue)}>
                      ${product?.l4_median_unit_price_max?.toFixed(2)}
                    </span>
                  </div>
                </Tooltip>
              }
            >
              <div className={css(styles.priceIcon)}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
            </OverlayTrigger>
          )}
          {mfrView && (
            <div
              className={css(styles.marketShareIcon)}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                const productId = product.global_product_id || product.id;
                window.open(`/market-share/${productId}`, '_blank');
              }}
              title="View Market Share"
            >
              <FontAwesomeIcon icon={faChartPie} />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          {viewGMV && !mfrView && (
            <UPBL5PercentageGMVBadge
              percentage={
                InvoiceAndOrderingGMV
                  ? (product.invoice_and_ordering_gmv_percentage / InvoiceAndOrderingGMV) * 100
                  : 0
              }
            />
          )}
          {viewScore && <UPBScoreBadge score={product.fsa_score || product.product_score} />}
        </div>
      </div>

      {/* Product Image - Clean, no overlays */}
      <Link to={productLink} target="_blank" className={css(styles.imageLink)}>
        <div className={css(styles.productImageContainer)}>
          <Card.Img className={css(styles.productImage)} src={productImageUrl} />
        </div>
      </Link>

      {/* Product Details */}
      <div className={css(styles.productDetails)}>
        <Link to={productLink} target="_blank" className={css(styles.displayName)}>
          {titleCase(truncate(product.name, { length: DISPLAY_NAME_LENGTH, omission: '' }))}
          {product.name.length > DISPLAY_NAME_LENGTH && (
            <OverlayTrigger overlay={<Tooltip>{titleCase(product.name)}</Tooltip>}>
              <span>...</span>
            </OverlayTrigger>
          )}
        </Link>

        <div className={css(styles.brandName)}>{product.brand_name}</div>

        <div className={css(styles.productMeta)}>
          {product.pack_size && <div className={css(styles.metaItem)}>{product.pack_size}</div>}
          {product.manufacturer_sku && (
            <div className={css(styles.metaItem)}>{product.manufacturer_sku}</div>
          )}
        </div>
      </div>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    width: 200,
    borderRadius: 14,
    margin: '10px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${borderGray}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    backgroundColor: white,
    ':hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 16px 32px rgba(0, 0, 0, 0.16)',
      border: `1px solid ${warmBlue}`,
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    minHeight: '40px',
    borderBottom: `1px solid ${borderLightGray}`,
    backgroundColor: white,
  },
  imageLink: {
    textDecoration: 'none',
    display: 'block',
  },
  productImageContainer: {
    position: 'relative',
    width: '100%',
    height: '170px',
    backgroundColor: ghostWhite,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '14px',
    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    ':hover': {
      transform: 'scale(1.08)',
    },
  },
  productDetails: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    backgroundColor: white,
  },
  displayName: {
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '1.4',
    color: bodyText,
    display: 'block',
    textTransform: 'capitalize',
    minHeight: '40px',
    transition: 'color 0.2s ease',
    ':hover': {
      color: warmBlue,
    },
  },
  brandName: {
    fontSize: '13px',
    fontWeight: '600',
    color: slateGrey,
    lineHeight: '1.3',
    marginBottom: '2px',
  },
  productMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingTop: '4px',
    borderTop: `1px solid ${borderLightGray}`,
  },
  metaItem: {
    fontSize: '12px',
    fontWeight: '500',
    color: anchovy,
    lineHeight: '1.4',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  editIcon: {
    fontSize: '14px',
    color: anchovy,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      opacity: 0.7,
      transform: 'scale(1.1)',
    },
  },
  rebateIcon: {
    color: softBlue,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    ':hover': {
      color: warmBlue,
      transform: 'scale(1.1)',
    },
  },
  priceIcon: {
    fontSize: '14px',
    color: softGreen,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    ':hover': {
      color: softGreenDark,
      transform: 'scale(1.15)',
    },
  },
  marketShareIcon: {
    fontSize: '14px',
    color: softPurple,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    userSelect: 'none',
    ':hover': {
      color: softPurpleDark,
      transform: 'scale(1.2)',
    },
    ':active': {
      transform: 'scale(1.1)',
    },
  },
  pricingTooltip: {
    textAlign: 'left',
    padding: '4px 0',
  },
  tooltipTitle: {
    fontWeight: '600',
    fontSize: '12px',
    marginBottom: '6px',
    color: white,
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    paddingBottom: '4px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 0',
    gap: '12px',
  },
  tooltipLabel: {
    fontSize: '11px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  tooltipValue: {
    fontSize: '12px',
    fontWeight: '600',
    color: white,
  },
  tooltipSeparator: {
    borderTop: `1px solid ${white}`,
    margin: '8px 0',
    opacity: 0.3,
  },
});

UPBProductCatalogCard.propTypes = {
  product: PropTypes.object,
  productType: PropTypes.string,
  invoicePlusOrderingGMVValue: PropTypes.number,
};

export default UPBProductCatalogCard;
